import { LitElement, html, css, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import { createLogger } from '../ns-services/logger.js';

const logger = createLogger('ns-icon');

const __iconCache: Record<string, string> = {};

@customElement(NSIcon.is)
class NSIcon extends LitElement {
  static get is() {
    return 'ns-icon';
  }

  @property({ type: String })
  icon: string | null = null;

  @state()
  private __svgContent: string = '';

  static get styles() {
    return [
      css`
        :host {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          position: relative;
          vertical-align: middle;
          height: var(--icon-height, 24px);
          width: var(--icon-width, 24px);
        }

        /* Figma's icon bubbles: a purple disc with a white glyph, or a green one with a dark glyph */
        :host([bubble]) {
          border-radius: var(--wa-border-radius-circle);
          background: var(--ns-theme-purple);
          color: var(--ns-theme-white);
        }

        :host([bubble='green']) {
          background: var(--ns-theme-green);
          color: var(--ns-theme-type);
        }

        :host svg {
          display: block;
          width: 100%;
          height: 100%;

          color: currentColor;
        }

        :host([bubble]) svg {
          width: 52%;
          height: 52%;
        }

        /* A heavier line at the same on-screen weight, whatever the icon's viewBox or own stroke-width */
        :host([bold]) svg [stroke]:not([stroke='none']) {
          stroke-width: 2px;
          vector-effect: non-scaling-stroke;
        }
      `,
    ];
  }

  render() {
    return html`
      ${this.__svgContent ? unsafeHTML(this.__svgContent) : ''}
    `;
  }

  getSrcFromIcon(icon: string) {
    return `/images/icons/${icon}.svg`;
  }

  private async _loadSVG() {
    if (!this.icon) {
      this.__svgContent = '';
      return;
    }

    if (__iconCache[this.icon]) {
      this.__svgContent = __iconCache[this.icon];
      return;
    }

    const svgPath = this.getSrcFromIcon(this.icon);

    try {
      const response = await fetch(svgPath);
      if (!response.ok) {
        throw new Error(`Failed to load SVG: ${response.statusText} (${response.status}) from ${svgPath}`);
      }
      const svgText = await response.text();
      this.__svgContent = svgText;
      __iconCache[this.icon] = svgText; // Cache the loaded SVG
    } catch (error) {
      logger.error('Error loading SVG:', error);
      this.__svgContent =
        '<svg viewBox="0 0 24 24" fill="currentColor" role="img" aria-label="error-icon"><path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="red"/></svg>'; // Fallback error icon
    }
  }

  protected updated(changedProperties: PropertyValues<this>) {
    if (changedProperties.has('icon') && this.icon) {
      this._loadSVG();
    } else if (changedProperties.has('icon') && !this.icon) {
      this.__svgContent = '';
    }
  }
}
export { NSIcon };
